import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasConsented, setConsent, isAnalyticsAllowed } from '../../utils/cookie-consent';
import { trackConsentResponse, inviaCampagnaSePresente } from '../../utils/analytics';
import { loadUmami, fermaUmami } from '../../utils/load-umami';
import * as Sentry from '@sentry/react';
import { log } from '../../utils/logger';
import './CookieBanner.css';

function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(!hasConsented());

  if (!visible) return null;

  const handleAcceptAll = () => {
    log.info('CookieBanner', 'Consent given');
    setConsent(true);
    setVisible(false);
    /*
     * Lo script parte ADESSO, non al prossimo ricaricamento.
     *
     * Da quando `loadUmami` aspetta il consenso, la chiamata di `main.tsx`
     * esce a vuoto per chi non ha ancora risposto: senza questa riga, chi
     * accetta resterebbe non misurato per tutta la sessione — cioe' proprio la
     * prima, quella che dice da dove e' arrivato.
     */
    /*
     * L'evento parte QUANDO lo script c'e', non un istante prima.
     *
     * `trackConsentResponse` usa `window.umami` direttamente e se non lo trova
     * esce in silenzio: chiamandola subito dopo l'iniezione, l'accettazione
     * finiva nel vuoto mentre lo script si stava ancora scaricando. Il piano
     * vedeva questo difetto solo sul ramo del rifiuto; vale identico qui.
     */
    /* E con l'accettazione parte anche la campagna d'arrivo, catturata a
       inizio visita e tenuta in memoria fino a questo istante (campagna.ts). */
    loadUmami(undefined, () => { trackConsentResponse(true); inviaCampagnaSePresente(); });
    // Initialize Sentry now that consent is given
    if (import.meta.env.VITE_SENTRY_DSN && !Sentry.isInitialized()) {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        tracesSampleRate: 0.1,
      });
    }
  };

  const handleRejectAnalytics = () => {
    log.info('CookieBanner', 'Consent denied');
    setConsent(false);
    setVisible(false);
    /*
     * Il rifiuto NON si misura, e non e' una dimenticanza.
     *
     * Con il consenso negato non si puo' scrivere sul terminale ne' misurare:
     * prima la chiamata c'era e, dopo che lo script ha smesso di partire senza
     * consenso, sarebbe comunque finita nel vuoto — una riga che prometteva un
     * dato che non arriva. Il tasso di rifiuto si leggera' dai contatori
     * aggregati lato server (Fase 1), che non hanno bisogno di consenso.
     *
     * E si SPEGNE quello che potrebbe essere gia' acceso: il banner si puo'
     * riaprire dalle impostazioni dopo aver accettato, e in quel caso lo
     * script e' gia' in pagina con i suoi ascoltatori attaccati.
     */
    fermaUmami();
  };

  return (
    <div className="cookie-banner">
      <div className="cookie-banner-content">
        <p className="cookie-banner-text">{t('cookies.message')}</p>
        <div className="cookie-banner-actions">
          <button
            className="cookie-btn cookie-btn-reject"
            onClick={handleRejectAnalytics}
            type="button"
          >
            {t('cookies.rejectAnalytics')}
          </button>
          <button
            className="cookie-btn cookie-btn-accept"
            onClick={handleAcceptAll}
            type="button"
          >
            {t('cookies.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieBanner;
